import React, { useCallback, useEffect, useRef, useState } from "react";
//@ts-ignore
import BpmnViewer from "bpmn-js/lib/NavigatedViewer";
import { Box, Button, Collapse, Dialog, DialogContent, Divider, IconButton, List, ListItemButton, ListItemIcon, ListItemText, Stack, Tooltip, Typography } from "@mui/material";
import { CloudDone, CloudDownloadOutlined, Folder, FolderSpecial, KeyboardArrowDown, OpenInFull, Restore, ViewInAr, ZoomIn, ZoomOut } from "@mui/icons-material";
import { formatDate } from "common/utilities";
import { getTranslation } from "common";
import { useTranslations } from "hooks";
import DialogHeader from "components/dialogs/DialogHeader";
import { ProcessModelFolderType, RevisionModelType, SelectedProcessModelType } from "model/processModel";
import { bpmnDiagramThemeSx } from "utils/bpmnDiagramStyles";
import { ProcessLibraryRow } from "./ProcessLibraryTable";

interface ProcessPreviewPanelProps {
  selectedRow: ProcessLibraryRow | null;
  revisionList: RevisionModelType[];
  selectedRevision: RevisionModelType | null;
  onSelectRevision: (revision: RevisionModelType) => void;
  onSelectCurrentVersion: () => void;
  onRestoreRevision: (revision: RevisionModelType) => void;
  onZoom: (ratio: number) => void;
  isAdmin?: boolean;
  folderChildren: ProcessModelFolderType[];
  folderModels: SelectedProcessModelType[];
  modelXml: string;
}

const ProcessPreviewPanel: React.FC<ProcessPreviewPanelProps> = ({
  selectedRow,
  revisionList,
  selectedRevision,
  onSelectRevision,
  onSelectCurrentVersion,
  onRestoreRevision,
  onZoom,
  isAdmin,
  folderChildren,
  folderModels,
  modelXml,
}) => {
  const translations = useTranslations();
  const [revisionsOpen, setRevisionsOpen] = useState(true);
  const [modelDialogOpen, setModelDialogOpen] = useState(false);
  const dialogViewerRef = useRef<any>(null);

  const hasFolderContents = folderChildren.length > 0 || folderModels.length > 0;

  const destroyDialogViewer = useCallback(() => {
    dialogViewerRef.current?.destroy();
    dialogViewerRef.current = null;
  }, []);

  const openDialogModel = useCallback(() => {
    if (!modelXml) {
      return;
    }
    const canvas = document.getElementById("previewer-dialog-canvas");
    if (!canvas) {
      return;
    }
    destroyDialogViewer();
    canvas.innerHTML = "";
    dialogViewerRef.current = new BpmnViewer({
      container: canvas,
      readOnly: true,
      selectable: false,
      additionalModules: [],
    });
    (async () => {
      try {
        await dialogViewerRef.current.importXML(modelXml);
        const canvasApi: any = dialogViewerRef.current.get("canvas");
        canvasApi.zoom("fit-viewport");
      } catch (error) {
        console.error(error);
      }
    })();
  }, [destroyDialogViewer, modelXml]);

  const zoomDialogModel = useCallback((ratio: number) => {
    const canvasApi: any = dialogViewerRef.current?.get("canvas");
    if (!canvasApi) {
      return;
    }
    canvasApi.zoom(canvasApi.zoom() * ratio);
  }, []);

  useEffect(() => {
    if (!modelDialogOpen) {
      destroyDialogViewer();
    }
  }, [destroyDialogViewer, modelDialogOpen]);

  return (
  <React.Fragment>
  <Stack
    sx={{
      height: "100%",
      minHeight: 0,
      border: 1,
      borderColor: "divider",
      borderRadius: 2,
      bgcolor: "background.paper",
      overflow: "hidden",
    }}
  >
    <Stack flex={1} minHeight={0} alignItems="center" justifyContent="center" spacing={2} p={2.5}>
      {!selectedRow ? (
        <React.Fragment>
          <FolderSpecial sx={{ color: "primary.main", fontSize: 112, opacity: 0.9 }} />
          <Box textAlign="center">
            <Typography variant="h6">{getTranslation(translations, "process_library.select_folder_or_model")}</Typography>
            <Typography color="text.secondary">{getTranslation(translations, "process_library.preview_details")}</Typography>
          </Box>
        </React.Fragment>
      ) : selectedRow.kind === "folder" ? (
        hasFolderContents ? (
          <Stack alignSelf="stretch" spacing={1.5} sx={{ width: "100%", maxHeight: "100%", overflow: "auto" }}>
            <Box textAlign="center">
              <Typography variant="h6">{selectedRow.folder.name}</Typography>
              <Typography color="text.secondary">{getTranslation(translations, "process_library.folder")}</Typography>
            </Box>
            {folderModels.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ pl: 1, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  {getTranslation(translations, "menu.left.process")} ({folderModels.length})
                </Typography>
                <List dense disablePadding>
                  {folderModels.map((model) => (
                    <ListItemButton key={model.id} sx={{ borderRadius: 1 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        {model.modeltype ? (
                          <CloudDone fontSize="small" sx={{ color: "primary.main" }} />
                        ) : (
                          <CloudDownloadOutlined fontSize="small" sx={{ color: "primary.main" }} />
                        )}
                      </ListItemIcon>
                      <ListItemText primary={model.name || getTranslation(translations, "pm.placeholder.untitledprocessmodel")} primaryTypographyProps={{ variant: "body2" }} />
                    </ListItemButton>
                  ))}
                </List>
              </Box>
            )}
            {folderChildren.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ pl: 1, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  {getTranslation(translations, "process_library.subfolders")} ({folderChildren.length})
                </Typography>
                <List dense disablePadding>
                  {folderChildren.map((folder) => (
                    <ListItemButton key={folder.id} sx={{ borderRadius: 1 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <Folder fontSize="small" sx={{ color: "primary.main" }} />
                      </ListItemIcon>
                      <ListItemText primary={folder.name} primaryTypographyProps={{ variant: "body2" }} />
                    </ListItemButton>
                  ))}
                </List>
              </Box>
            )}
          </Stack>
        ) : (
          <React.Fragment>
            <FolderSpecial sx={{ color: "primary.main", fontSize: 96 }} />
            <Box textAlign="center">
              <Typography variant="h6">{selectedRow.folder.name}</Typography>
              <Typography color="text.secondary">{getTranslation(translations, "process_library.folder")}</Typography>
            </Box>
          </React.Fragment>
        )
      ) : (
        <React.Fragment>
          <ViewInAr sx={{ color: "primary.main", fontSize: 72, display: { lg: "none" } }} />
          {!!modelXml && (
            <Stack direction="row" spacing={1} alignSelf="flex-end">
              {isAdmin && !selectedRow.model.modeltype && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Restore />}
                  disabled={!selectedRevision}
                  onClick={() => selectedRevision && onRestoreRevision(selectedRevision)}
                >
                  {getTranslation(translations, "process_library.restore_version")}
                </Button>
              )}
              <Tooltip title={getTranslation(translations, "pm.icon.zoom_in")}>
                <IconButton size="small" onClick={() => onZoom(1.2)}>
                  <ZoomIn fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={getTranslation(translations, "pm.icon.zoom_out")}>
                <IconButton size="small" onClick={() => onZoom(0.8)}>
                  <ZoomOut fontSize="small" />
                </IconButton>
              </Tooltip>
              <Button
                size="small"
                variant="outlined"
                startIcon={<OpenInFull />}
                onClick={() => setModelDialogOpen(true)}
              >
                {getTranslation(translations, "process_library.expand")}
              </Button>
            </Stack>
          )}
          <Box
            id="previewer-canvas"
            sx={{
              width: "100%",
              height: "100%",
              minHeight: 220,
              "& .djs-palette": { display: "none" },
            }}
          />
          <Box textAlign="center">
            <Typography variant="h6">{selectedRow.model.name}</Typography>
            <Typography color="text.secondary">
              {selectedRevision ? formatDate(selectedRevision.creation_time) : getTranslation(translations, "process_library.current_version")}
            </Typography>
          </Box>
        </React.Fragment>
      )}
    </Stack>
    {selectedRow?.kind === "model" && (
      <React.Fragment>
        <Divider />
        <Stack sx={{ flexShrink: 0 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            onClick={() => setRevisionsOpen((open) => !open)}
            sx={{
              px: 2,
              py: 1.25,
              cursor: "pointer",
              userSelect: "none",
              "&:hover": { backgroundColor: "action.hover" },
            }}
            aria-expanded={revisionsOpen}
            role="button"
          >
            <KeyboardArrowDown
              sx={{
                transition: "transform 0.2s",
                transform: revisionsOpen ? "rotate(0deg)" : "rotate(-90deg)",
              }}
            />
            <Typography variant="subtitle2">{getTranslation(translations, "pm.button.revisions")}</Typography>
          </Stack>
          <Collapse in={revisionsOpen} timeout="auto" unmountOnExit>
            <Stack spacing={1} px={2} pb={2} sx={{ maxHeight: 190, overflow: "auto" }}>
              <Button
                size="small"
                variant={!selectedRevision ? "contained" : "outlined"}
                onClick={onSelectCurrentVersion}
                sx={{ alignSelf: "center" }}
              >
                {getTranslation(translations, "process_library.current_version")}
              </Button>
              {revisionList.map((revision) => (
                <Button
                  key={`process-model-${revision.idprocessmodel}-revision-${revision.id}`}
                  size="small"
                  fullWidth
                  variant={selectedRevision?.id === revision.id ? "contained" : "text"}
                  onClick={() => onSelectRevision(revision)}
                >
                  {formatDate(revision.creation_time)}
                </Button>
              ))}
              {!revisionList.length && (
                <Typography variant="caption" color="text.secondary" textAlign="center">
                  {getTranslation(translations, "process_library.no_previous_versions")}
                </Typography>
              )}
            </Stack>
          </Collapse>
        </Stack>
      </React.Fragment>
    )}
  </Stack>
  <Dialog
    open={modelDialogOpen}
    onClose={() => setModelDialogOpen(false)}
    maxWidth="lg"
    fullWidth
    TransitionProps={{ onEntered: openDialogModel, onExited: destroyDialogViewer }}
  >
    <DialogHeader onClose={() => setModelDialogOpen(false)}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
        <Typography noWrap>{selectedRow?.kind === "model" ? selectedRow.model.name : getTranslation(translations, "pm.modelname.label.caption")}</Typography>
        <Stack direction="row" gap={0.5}>
          <Tooltip title={getTranslation(translations, "pm.icon.zoom_in")}>
            <IconButton size="small" onClick={() => zoomDialogModel(1.2)}>
              <ZoomIn fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={getTranslation(translations, "pm.icon.zoom_out")}>
            <IconButton size="small" onClick={() => zoomDialogModel(0.8)}>
              <ZoomOut fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </DialogHeader>
    <DialogContent sx={{ p: 0, height: "75vh" }}>
      <Box
        id="previewer-dialog-canvas"
        className="canvas"
        sx={{
          ...bpmnDiagramThemeSx,
          "& .djs-palette": { display: "none" },
          width: "100%",
          height: "100%",
        }}
      />
    </DialogContent>
  </Dialog>
  </React.Fragment>
  );
};

export default ProcessPreviewPanel;
